import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploadSelectEvent } from 'primereact/fileupload';
import { confirmDialog } from 'primereact/confirmdialog';
import { OcrService } from '@app/services/OcrService';
import { OcrConversionInterface } from '@app/interfaces/OcrConversionInterface';
import { ToastMessageInterface } from '@app/interfaces/ToastMessageInterface';
import OcrScreen from './OcrScreen';

export interface OcrScreenHocProps {
    showToast: (toast: ToastMessageInterface) => void;
}

const OcrScreenHoc: React.FunctionComponent<OcrScreenHocProps> = ({ showToast }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [conversions, setConversions] = useState<OcrConversionInterface[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [sourceUrl, setSourceUrl] = useState<string>('');
    const [sourceType, setSourceType] = useState<'pdf' | 'image'>('pdf');
    const [includeImages, setIncludeImages] = useState<boolean>(true);
    const [processing, setProcessing] = useState(false);
    const [previewDialogVisible, setPreviewDialogVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState<string>('');
    const [previewConversion, setPreviewConversion] = useState<OcrConversionInterface | null>(null);
    const [previewTabIndex, setPreviewTabIndex] = useState(0);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [activeTab, setActiveTab] = useState(0);

    const ocrService = OcrService.getInstance();

    useEffect(() => {
        loadConversions();
    }, [page]);

    const loadConversions = async () => {
        try {
            setLoading(true);
            const response = await ocrService.listConversions(page, 10);
            setConversions(response.items);
            setTotalRecords(response.total);
        } catch (error: any) {
            showToast({
                severity: 'error',
                summary: t('ocr_screen.load_error'),
                detail: error.message
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (event: FileUploadSelectEvent) => {
        const file = event.files[0];
        if (file) {
            setSelectedFile(file);
            // Auto-detect source type based on file extension
            const extension = file.name.split('.').pop()?.toLowerCase();
            if (extension === 'pdf') {
                setSourceType('pdf');
            } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
                setSourceType('image');
            }
        }
    };

    const handleProcess = async (fileUploadRef: React.RefObject<any>) => {
        if (activeTab === 0 && !selectedFile) {
            showToast({
                severity: 'warn',
                summary: t('ocr_screen.validation_error'),
                detail: t('ocr_screen.no_file_selected')
            });
            return;
        }

        if (activeTab === 1 && !sourceUrl) {
            showToast({
                severity: 'warn',
                summary: t('ocr_screen.validation_error'),
                detail: t('ocr_screen.no_url_provided')
            });
            return;
        }

        try {
            setProcessing(true);
            await ocrService.processOcr({
                sourceType,
                file: activeTab === 0 ? selectedFile! : undefined,
                sourceUrl: activeTab === 1 ? sourceUrl : undefined,
                saveToS3: false,
                includeImages,
                model: 'mistral-ocr-latest'
            });

            showToast({
                severity: 'success',
                summary: t('ocr_screen.process_success'),
                detail: t('ocr_screen.process_success_detail')
            });

            // Reset form
            setSelectedFile(null);
            setSourceUrl('');

            // Clear the file upload component
            if (fileUploadRef.current) {
                fileUploadRef.current.clear();
            }

            // Reload conversions
            await loadConversions();
        } catch (error: any) {
            showToast({
                severity: 'error',
                summary: t('ocr_screen.process_error'),
                detail: error.message
            });
        } finally {
            setProcessing(false);
        }
    };

    const handlePreview = async (conversion: OcrConversionInterface) => {
        try {
            setPreviewConversion(conversion);
            if (conversion.resultMarkdown) {
                setPreviewContent(conversion.resultMarkdown);
                setPreviewDialogVisible(true);
            } else {
                const markdown = await ocrService.downloadMarkdown(conversion.id);
                setPreviewContent(markdown);
                setPreviewDialogVisible(true);
            }
        } catch (error: any) {
            showToast({
                severity: 'error',
                summary: t('ocr_screen.preview_error'),
                detail: error.message
            });
        }
    };

    const handleDownload = async (conversion: OcrConversionInterface) => {
        try {
            const markdown = await ocrService.downloadMarkdown(conversion.id);
            const blob = new Blob([markdown], { type: 'text/markdown' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ocr-result-${conversion.id}.md`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            showToast({
                severity: 'success',
                summary: t('ocr_screen.download_success'),
                detail: t('ocr_screen.download_success_detail')
            });
        } catch (error: any) {
            showToast({
                severity: 'error',
                summary: t('ocr_screen.download_error'),
                detail: error.message
            });
        }
    };

    const handleCopyToClipboard = useCallback(() => {
        if (previewContent) {
            navigator.clipboard.writeText(previewContent).then(() => {
                showToast({
                    severity: 'success',
                    summary: t('globals.copiedToClipboardSuccess'),
                    detail: ''
                });
            }).catch((error) => {
                showToast({
                    severity: 'error',
                    summary: t('globals.copiedToClipboardFailed'),
                    detail: error.message
                });
            });
        }
    }, [previewContent, showToast, t]);

    const handleCopyMarkdownToClipboard = async (conversion: OcrConversionInterface) => {
        try {
            let markdown = conversion.resultMarkdown;

            // If markdown is not in the response, fetch it
            if (!markdown) {
                markdown = await ocrService.downloadMarkdown(conversion.id);
            }

            await navigator.clipboard.writeText(markdown);

            showToast({
                severity: 'success',
                summary: t('globals.copiedToClipboardSuccess'),
                detail: ''
            });
        } catch (error: any) {
            showToast({
                severity: 'error',
                summary: t('globals.copiedToClipboardFailed'),
                detail: error.message
            });
        }
    };

    const handleDelete = (conversion: OcrConversionInterface) => {
        confirmDialog({
            message: t('ocr_screen.delete_confirmation_message'),
            header: t('ocr_screen.delete_confirmation_header'),
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await ocrService.deleteConversion(conversion.id);
                    showToast({
                        severity: 'success',
                        summary: t('ocr_screen.delete_success'),
                        detail: t('ocr_screen.delete_success_detail')
                    });
                    loadConversions();
                } catch (error: any) {
                    showToast({
                        severity: 'error',
                        summary: t('ocr_screen.delete_error'),
                        detail: error.message
                    });
                }
            }
        });
    };

    const handlePreviewDialogHide = () => {
        setPreviewDialogVisible(false);
        setPreviewTabIndex(0);
    };

    return (
        <OcrScreen
            form={{
                selectedFile,
                sourceUrl,
                setSourceUrl,
                sourceType,
                setSourceType,
                includeImages,
                setIncludeImages,
                processing,
                activeTab,
                setActiveTab
            }}
            conversionsList={{
                loading,
                conversions,
                page,
                setPage,
                totalRecords
            }}
            preview={{
                visible: previewDialogVisible,
                content: previewContent,
                conversion: previewConversion,
                tabIndex: previewTabIndex,
                setTabIndex: setPreviewTabIndex
            }}
            onFileSelect={handleFileSelect}
            onProcess={handleProcess}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onCopyToClipboard={handleCopyToClipboard}
            onCopyMarkdownToClipboard={handleCopyMarkdownToClipboard}
            onDelete={handleDelete}
            onLoadConversions={loadConversions}
            onPreviewDialogHide={handlePreviewDialogHide}
        />
    );
};

export default OcrScreenHoc;
